package ui.console

import AppBuildConfig
import androidx.compose.runtime.*
import io.Repository
import io.ktor.client.plugins.websocket.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.utils.io.core.*
import io.ktor.websocket.*
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.cancelAndJoin
import kotlinx.coroutines.channels.consumeEach
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDateTime
import org.ssoft.common.tools.Authorization
import org.ssoft.common.tools.nowDateTime
import org.ssoft.common.tools.nowMilliseconds
import org.w3c.dom.get
import org.w3c.dom.set
import srializable.JSON_INSTANCE
import tk.mallumo.common.msg.Message
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.log.logINFO
import tk.mallumo.utils.mutableStateFlowOf
import kotlin.time.Clock

@VM
class ConsoleVM : NavigationViewModel() {

    private var lastDeviceSession: DefaultClientWebSocketSession? = null
    private lateinit var scopeUi: ConsoleScope

    override fun onRelease() = Unit

    var connectionState by mutableStateOf("idle")
        private set

    private val _history: MutableStateFlow<List<String>> = mutableStateFlowOf(listOf())
    val history = _history.asStateFlow()

    private fun readHistory() {
        val historyJson = window.localStorage["cmd-history"]
            ?: "[]"
        _history.value = JSON_INSTANCE.decodeFromString<MutableList<String>>(historyJson)

    }

    private fun storeCommand(cmd: String) {
        val data = _history.value.toMutableList().apply {
            removeAll { it == cmd.trim() }
            add(0, cmd)
        }
        with(data) {
            _history.value = this
            window.localStorage["cmd-history"] = JSON_INSTANCE.encodeToString(this.take(100))
        }
    }

    fun init(consoleScope: ConsoleScope) {
        this.scopeUi = consoleScope
        scope.launch {
            readHistory()
        }
    }

    class MsgHolder(
        val id: Long,
        val dt: LocalDateTime,
        val send: Message.Cmd,

        ) {
        var sendOk: Boolean? by mutableStateOf(null)
        var receive: Message.Cmd? by mutableStateOf(null)
        var message: String by mutableStateOf("")
    }

    var messages by mutableStateOf(listOf<MsgHolder>())
        private set

    sealed interface Action {

        data class Commit(val cmd: String) : Action

        companion object {

            @Composable
            fun rememberAction(vm: ConsoleVM): (Action) -> Unit {

                return remember {
                    { act ->
                        when (act) {
                            is Commit -> vm.commit(act.cmd.trim())
                        }
                    }
                }
            }
        }
    }

    private fun commit(cmd: String) {

        val command = Message.Cmd(
            id = Clock.nowMilliseconds(),
            source = scopeUi.id,
            target = scopeUi.mac,
            statusInt = 0,
            statusStr = "",
            command = cmd
        )
        messages += MsgHolder(
            id = command.id,
            dt = Clock.nowDateTime(),
            send = command
        )
        scope.launch {
            lastDeviceSession?.send(Message.encode(command))
            storeCommand(cmd)
        }
    }

    private var connectionJob: Job? = null

    fun connect() {
        connectionJob = scope.launch {
            makeConnection()
        }
    }

    fun disconnect() {
        scope.launch {
            runCatching {
                connectionJob?.cancelAndJoin()
            }.onFailure {
                it.printStackTrace()
            }
            connectionState = "disconnected"
        }
    }

    private suspend fun makeConnection() =
        coroutineScope {
            connectionState = "connecting"
            runCatching {
                val (_, pass) = Authorization.createAuth(scopeUi.id)
                Repository.api.client.ws().use {
                    it.webSocket(
                        method = HttpMethod.Get,
                        host = AppBuildConfig.HOST_BALANCER,
                        port = AppBuildConfig.PORT_BALANCER,
                        path = "/web/$pass",
                        request = {
                            url.protocol = URLProtocol.WSS
                            url.port = port
                        }
                    ) {
                        lastDeviceSession = this
                        connectionState = "connected"
                        incoming.consumeEach { message ->
                            if (message is Frame.Binary) {
                                handleMessage(message.readBytes())
                            }
                        }

                    }
                }
            }.onFailure {
                connectionState = "disconnected"
                lastDeviceSession = null
                logINFO("connection fail")
                it.printStackTrace()
            }
            connectionState = "disconnected"

            lastDeviceSession = null
        }

    private fun handleMessage(readBytes: ByteArray) {
        val (msg, data) = Message.decode(readBytes)
        when (msg) {
            is Message.Status -> {
                messages.firstOrNull { it.id == msg.id }?.apply {
                    sendOk = msg.statusInt == 0
                }
            }

            is Message.Cmd -> {
                messages.firstOrNull { it.id == msg.id }?.apply {
                    receive = msg
                    message = data?.takeIf { it.isNotEmpty() }?.decodeToString() ?: ""
                }
            }

            is Message.Task,
            is Message.DataReceive,
            is Message.DataSend,
            is Message.Displayshot,
            is Message.ReportConnection,
            is Message.ReportPlaylist,
            is Message.ReportSystem,
            is Message.ReportApp,
            is Message.GameAction,
            is Message.ReportGame,
            is Message.Screenshot -> Unit

        }
    }
}
